import React from "react";
import { v4 as uuid } from "uuid";
import { useHistory, useParams, useLocation } from "react-router-dom";
import { VehicleAccessFormView } from "./views";
import {
  VehicleAccessFormData,
  EditVehicleAccessFormProps,
  VehicleAccessFormParams,
  CreateVehicleAccessFormProps,
} from "./interfaces";
import { VehicleAccess } from "../../interfaces";
import { useVehiclesApi } from "../../hooks/use-vehicles";
import { useVehicleAccessApi } from "../../hooks/use-vehicle-access";

/**
 * A container to handle creating or editing a vehicle access
 *
 * It decides whether to show a create or edit form based on whether
 * there is an vehicle access ID present in the URL.
 */
export const VehicleAccessForm: React.FC = () => {
  const { accountId, vehicleId } = useParams<VehicleAccessFormParams>();
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- Typing this is a bit arduous
  const location = useLocation<any>();

  const {
    data, // list of vehicle
    error,
  } = useVehiclesApi(accountId, undefined, 1000);

  return vehicleId ? (
    <EditVehicleAccessForm
      id={vehicleId}
      accountId={accountId}
      vehicles={data}
      vehiclesError={error}
      created={location.state?.created}
    />
  ) : (
    <CreateVehicleAccessForm
      accountId={accountId}
      vehicles={data}
      vehiclesError={error}
    />
  );
};

const CreateVehicleAccessForm: React.FC<CreateVehicleAccessFormProps> = ({
  accountId,
  vehicles,
  vehiclesError,
}) => {
  const history = useHistory();
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [error, setError] = React.useState(false);
  const [success, setSuccess] = React.useState(false);
  const { createVehicleAccess } = useVehicleAccessApi();

  /**
   * Handle form submit
   *
   * Transforms the form data into the shape required by the API and posts it
   */
  const handleSubmit = async (data: VehicleAccessFormData) => {
    // If access to all vehicles is false, ensure at least one vehicle is selected
    if (
      !data.hasAccessToAllVehicles &&
      (!data.vehicles || data.vehicles.length === 0)
    ) {
      alert(
        "If you don't give access to all vehicles, you must select at least one vehicle."
      );
      return;
    }
    setIsSubmitting(true);
    try {
      const id = uuid();
      await createVehicleAccess({ ...data, id, accountId });

      setSuccess(true);
      /**
       * Navigate to edit screen for the newly-created vehicle
       */
      history.push(`/accounts/summary/${accountId}/vehicle-access/edit/${id}`, {
        created: true,
      });
    } catch (e) {
      setSuccess(false);
      setError(true);
      setIsSubmitting(false);
    }
  };

  return (
    <VehicleAccessFormView
      isSubmitting={isSubmitting}
      accountId={accountId}
      vehicles={vehicles}
      error={error || vehiclesError}
      success={success}
      onSubmit={handleSubmit}
    />
  );
};

const EditVehicleAccessForm: React.FC<EditVehicleAccessFormProps> = ({
  id,
  accountId,
  created,
  vehicles,
  vehiclesError,
}) => {
  const [data, setData] = React.useState<VehicleAccess | undefined>(undefined);

  const {
    loading,
    error,
    updateSuccess,
    getVehicleAccess,
    updateVehicleAccess,
    updating,
  } = useVehicleAccessApi();

  React.useEffect(() => {
    async function fetchData() {
      const data = await getVehicleAccess(id);
      setData(data);
    }

    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  /**
   * Handle form submit
   *
   * Transforms the form data into the shape required by the API and posts it
   */

  const handleSubmit = async (values: VehicleAccessFormData) => {
    if (!data) return;
    // If access to all vehicles is false, ensure at least one vehicle is selected
    if (
      !values.hasAccessToAllVehicles &&
      (!values.vehicles || values.vehicles.length === 0)
    ) {
      alert(
        "If you don't give access to all vehicles, you must select at least one vehicle."
      );
      return;
    }

    updateVehicleAccess({
      ...values,
      id,
      accountId: data.accountId,
    });
  };

  return (
    <VehicleAccessFormView
      vehicleAccess={data}
      vehicles={vehicles}
      loading={loading}
      error={vehiclesError || error}
      accountId={accountId}
      onSubmit={handleSubmit}
      isSubmitting={updating}
      success={created || updateSuccess}
      isEdit
    />
  );
};
